import AOS from 'aos';
import { useEffect, useState } from 'react';
import './App.css';
import { getIarcPdfDocument, viewIarcPdfDocument, getPropertyListings } from './services/service';
import { formatCurrency, formatDate, formatEMDDate } from './utils/helpers';


function NoticeBox({ heading, text, date, link, delay, pdfFileOne, pdfFileTwo, visibilityDate }) {
    // Enter Visibility Date in format of MM/DD/YYY
    const lastDate = new Date(visibilityDate);
    lastDate.setDate(lastDate.getDate() + 3);

    const currentDate = new Date();

    if(lastDate < currentDate) {
        return;
    }

    const openPDFsInNewTabs = (e, pdf1, pdf2) => {
        e.preventDefault();
      
        pdf1 && window.open(pdf1, '_blank', 'noopener,noreferrer');
        pdf2 && window.open(pdf2, '_blank', 'noopener,noreferrer');
      };

    return (
        <a href={link} onClick={(e) => !link && openPDFsInNewTabs(e, pdfFileOne, pdfFileTwo)} target='_blank' rel='noreferrer noopener' className='flex justify-center h-full' data-aos='fade-up' data-aos-delay={delay}>
            <div className='notice-box h-full overflow-hidden flex flex-col'>
                <div className='assets flex items-center'>
                    <p className='m-auto font-bold text-xl heading text-center uppercase'>{heading}</p>
                </div>
                <div className='info-box h-full w-full grow px-4'>
                    <p className='notice font-bold text-center'>{text}</p>
                </div>
                <div className='info-box flex flex-col items-center p-10'>
                    <p className='date font-bold text-center mb-8'>{date}</p>
                    <img src='/assets/EAuction.svg' alt='IARC' style={{ width: "115px", height: '115px' }} />
                </div>
            </div>
        </a>
    );
}

const AssetNewUi = () => {
    const [pdfData, setPdfData] = useState([]);
    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter states
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedPropertyType, setSelectedPropertyType] = useState('');
    const [selectedPropertySubtype, setSelectedPropertySubtype] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');

    // Sort state
    const [sortBy, setSortBy] = useState('');

    // View mode state
    const [viewMode, setViewMode] = useState('grid');

    useEffect(() => {
        AOS.init({
            duration: 1000
        });
    }, []);

    // Fetch property listings
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                setLoading(true);
                const res = await getPropertyListings();
                if(res.success) {
                    setProperties(res.success.data);
                    setFilteredProperties(res.success.data);
                }
            } catch (error) {
                console.error('Error fetching properties:', error);
                setError('Failed to load properties');
            } finally {
                setLoading(false);
            }
        };
        fetchProperties();
    }, []);

    // Extract unique values for filters
    const getUniqueStates = () => {
        return [...new Set(properties.map(p => p.state))].sort();
    };

    const getUniqueCities = () => {
        const cities = selectedState
            ? properties.filter(p => p.state === selectedState).map(p => p.city)
            : properties.map(p => p.city);
        return [...new Set(cities)].sort();
    };

    const getUniquePropertyTypes = () => {
        return [...new Set(properties.map(p => p.property_type))].sort();
    };

    const getUniquePropertySubtypes = () => {
        const subtypes = selectedPropertyType
            ? properties.filter(p => p.property_type === selectedPropertyType).map(p => p.sub_property_type)
            : properties.map(p => p.sub_property_type);
        return [...new Set(subtypes)].sort();
    };

    // Apply filters
    useEffect(() => {
        let filtered = [...properties];

        if (selectedState) {
            filtered = filtered.filter(p => p.state === selectedState);
        }

        if (selectedCity) {
            filtered = filtered.filter(p => p.city === selectedCity);
        }

        if (selectedPropertyType) {
            filtered = filtered.filter(p => p.property_type === selectedPropertyType);
        }

        if (selectedPropertySubtype) {
            filtered = filtered.filter(p => p.sub_property_type === selectedPropertySubtype);
        }

        if (minPrice) {
            filtered = filtered.filter(p => p.reserve_price >= parseInt(minPrice));
        }

        if (maxPrice) {
            filtered = filtered.filter(p => p.reserve_price <= parseInt(maxPrice));
        }

        // Apply sorting - create a new sorted array
        if (sortBy === 'auction_date') {
            filtered = [...filtered].sort((a, b) => {
                const dateA = new Date(a.auction_date);
                const dateB = new Date(b.auction_date);
                return dateA.getTime() - dateB.getTime();
            });
        } else if (sortBy === 'reserve_price') {
            filtered = [...filtered].sort((a, b) => {
                const priceA = parseFloat(a.reserve_price) || 0;
                const priceB = parseFloat(b.reserve_price) || 0;
                return priceA - priceB;
            });
        }

        setFilteredProperties(filtered);
    }, [selectedState, selectedCity, selectedPropertyType, selectedPropertySubtype, minPrice, maxPrice, sortBy, properties]);

    const handleClearFilters = () => {
        setSelectedState('');
        setSelectedCity('');
        setSelectedPropertyType('');
        setSelectedPropertySubtype('');
        setMinPrice('');
        setMaxPrice('');
    };

    async function fetchFile(fileName) {
        try {
            const response = await viewIarcPdfDocument(fileName);
            if (response.success) {
                const fileUrl = response.success.preSignedUrl;
                if (fileUrl) {
                    window.open(fileUrl, '_blank', 'noopener,noreferrer');
                } else {
                    console.error('File URL is invalid or not provided');
                }
            } else {
                throw new Error('File not found or invalid response');
            }
        } catch (error) {
            console.error('Error fetching file:', error);
        }
    }


    return (
        <>
            <div className='h-20'></div>
            <div className='heading-rounded m-auto text-center text-white font-bold py-4 w-11/12 md:w-3/5'>
                Assets For Sale
            </div>
            <div className='container px-6 xl:px-0 xl:w-4/5 2xl:container mx-auto py-20 min-h-screen'>

                {/* Loading Spinner */}
                {loading && (
                    <div className='flex flex-col items-center justify-center py-20'>
                        <div className='property-loader'></div>
                        <p className='mt-4 text-gray-600 text-lg font-medium'>Loading properties...</p>
                    </div>
                )}

                {/* Error Message */}
                {error && !loading && (
                    <div className='text-center py-20'>
                        <div className='text-red-500 text-xl font-semibold mb-4'>
                            {error}
                        </div>
                        <p className='text-gray-600'>Please try again later.</p>
                    </div>
                )}

                {/* Property Listing Section */}
                {!loading && !error && properties.length > 0 && (
                    <>
                        <div className='container px-6 xl:px-0 xl:w-11/12 2xl:container mx-auto py-10'>
                            <div className='grid grid-cols-1 lg:grid-cols-4 gap-8'>
                                {/* Filters Sidebar */}
                                <div className='lg:col-span-1'>
                                    <div className='property-filter-sidebar bg-white p-6 rounded-lg shadow-md sticky top-24'>
                                        <h3 className='text-xl font-bold mb-6 text-gray-800'>Filters</h3>

                                        {/* State Filter */}
                                        <div className='mb-6'>
                                            <label className='block text-xs font-semibold mb-2 text-gray-700'>State</label>
                                            <select
                                                value={selectedState}
                                                onChange={(e) => {
                                                    setSelectedState(e.target.value);
                                                    setSelectedCity('');
                                                }}
                                                className='property-filter-select w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500'
                                            >
                                                <option value=''>All States</option>
                                                {getUniqueStates().map(state => (
                                                    <option key={state} value={state}>{state}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* City Filter */}
                                        <div className='mb-6'>
                                            <label className='block text-xs font-semibold mb-2 text-gray-700'>City</label>
                                            <select
                                                value={selectedCity}
                                                onChange={(e) => setSelectedCity(e.target.value)}
                                                className='property-filter-select w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500'
                                            >
                                                <option value=''>All Cities</option>
                                                {getUniqueCities().map(city => (
                                                    <option key={city} value={city}>{city}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Property Type Filter */}
                                        {/* <div className='mb-6'>
                                            <label className='block text-xs font-semibold mb-2 text-gray-700'>Property type</label>
                                            <select
                                                value={selectedPropertyType}
                                                onChange={(e) => {
                                                    setSelectedPropertyType(e.target.value);
                                                    setSelectedPropertySubtype('');
                                                }}
                                                className='property-filter-select w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500'
                                            >
                                                <option value=''>All Types</option>
                                                {getUniquePropertyTypes().map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </div> */}

                                        {/* Property Subtype Filter */}
                                        {/* <div className='mb-6'>
                                            <label className='block text-xs font-semibold mb-2 text-gray-700'>Property Subtype</label>
                                            <select
                                                value={selectedPropertySubtype}
                                                onChange={(e) => setSelectedPropertySubtype(e.target.value)}
                                                className='property-filter-select w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500'
                                            >
                                                <option value=''>All Subtypes</option>
                                                {getUniquePropertySubtypes().map(subtype => (
                                                    <option key={subtype} value={subtype}>{subtype}</option>
                                                ))}
                                            </select>
                                        </div> */}

                                        {/* Price Range */}
                                        <div className='mb-6'>
                                            <label className='block text-xs font-semibold mb-2 text-gray-700'>Price Range</label>
                                            <div className='grid grid-cols-2 gap-3'>
                                                <select
                                                    value={minPrice}
                                                    onChange={(e) => setMinPrice(e.target.value)}
                                                    className='property-filter-select px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-xs'
                                                >
                                                    <option value=''>Min</option>
                                                    <option value='100000'>1L</option>
                                                    <option value='200000'>2L</option>
                                                    <option value='500000'>5L</option>
                                                    <option value='1000000'>10L</option>
                                                    <option value='2000000'>20L</option>
                                                    <option value='5000000'>50L</option>
                                                    <option value='10000000'>1Cr</option>
                                                    <option value='20000000'>2Cr</option>
                                                    <option value='50000000'>5Cr</option>
                                                    <option value='100000000'>10Cr</option>
                                                    <option value='200000000'>20Cr</option>
                                                    <option value='500000000'>50Cr</option>
                                                </select>
                                                <select
                                                    value={maxPrice}
                                                    onChange={(e) => setMaxPrice(e.target.value)}
                                                    className='property-filter-select px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-xs'
                                                >
                                                    <option value=''>Max</option>
                                                    <option value='100000'>1L</option>
                                                    <option value='200000'>2L</option>
                                                    <option value='500000'>5L</option>
                                                    <option value='1000000'>10L</option>
                                                    <option value='2000000'>20L</option>
                                                    <option value='5000000'>50L</option>
                                                    <option value='10000000'>1Cr</option>
                                                    <option value='20000000'>2Cr</option>
                                                    <option value='50000000'>5Cr</option>
                                                    <option value='100000000'>10Cr</option>
                                                    <option value='200000000'>20Cr</option>
                                                    <option value='500000000'>50Cr</option>
                                                    <option value='1000000000'>100Cr</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Clear Button */}
                                        <button
                                            onClick={handleClearFilters}
                                            className='property-clear-btn w-full bg-white border-2 border-gray-800 text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-800 hover:text-white transition-all duration-300'
                                        >
                                            CLEAR
                                        </button>
                                    </div>
                                </div>

                                {/* Property Listing */}
                                <div className='lg:col-span-3'>
                                    {/* Sort and View Controls */}
                                    <div className='flex flex-wrap items-center justify-between mb-6 gap-4'>
                                        <div className='flex items-center gap-4'>
                                            <span className='text-gray-600 font-medium flex items-center gap-2'>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M3 7h18M3 12h18M3 17h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                                Sort by:
                                            </span>
                                            <button
                                                onClick={() => setSortBy('auction_date')}
                                                className={`property-sort-btn px-4 py-2 font-semibold ${
                                                    sortBy === 'auction_date' ? 'active' : ''
                                                }`}
                                            >
                                                AUCTION DATE
                                            </button>
                                            <button
                                                onClick={() => setSortBy('reserve_price')}
                                                className={`property-sort-btn px-4 py-2 font-semibold ${
                                                    sortBy === 'reserve_price' ? 'active' : ''
                                                }`}
                                            >
                                                RESERVE PRICE
                                            </button>
                                        </div>

                                        {/* View Mode Toggle */}
                                        <div className='flex items-center gap-2'>
                                            <button
                                                onClick={() => setViewMode('grid')}
                                                className={`property-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                            >
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                    <rect x="3" y="3" width="8" height="8" rx="1"/>
                                                    <rect x="13" y="3" width="8" height="8" rx="1"/>
                                                    <rect x="3" y="13" width="8" height="8" rx="1"/>
                                                    <rect x="13" y="13" width="8" height="8" rx="1"/>
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => setViewMode('list')}
                                                className={`property-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                                            >
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                    <rect x="3" y="5" width="18" height="2" rx="1"/>
                                                    <rect x="3" y="11" width="18" height="2" rx="1"/>
                                                    <rect x="3" y="17" width="18" height="2" rx="1"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Property Cards */}
                                    {filteredProperties.length === 0 ? (
                                        <div className='text-center py-20 text-gray-500'>
                                            <p className='text-xl'>No properties found matching your filters.</p>
                                        </div>
                                    ) : (
                                        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                                            {filteredProperties.map((property) => (
                                                <div
                                                    key={property.id}
                                                    className={`property-card bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${property.cancelled ? 'cancelled' : ''}`}
                                                    data-aos='fade-up'
                                                >
                                                    {/* Cancelled Watermark */}
                                                    {property.cancelled && (
                                                        <img
                                                            src='/assets/cancelled%20icon%20png.png'
                                                            alt='Auction cancelled'
                                                            className='property-cancelled-watermark'
                                                        />
                                                    )}

                                                    <div className={`p-6 ${property.cancelled ? 'property-cancelled-body' : ''}`}>
                                                        {/* Location Header */}
                                                        <div className='flex items-start gap-3 mb-4'>
                                                            <div className='property-location-icon'>
                                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                {/* <h3 className='text-xl font-bold text-blue-900'>{property.title}</h3> */}
                                                                <h3 className='text-gray-600 font-bold text-green'>{property.state}</h3>
                                                            </div>
                                                        </div>

                                                        {/* Property Details */}
                                                        <div className='space-y-3 mb-6'>
                                                            {/* <div className='flex flex-col gap-2 text-gray-700'>
                                                                <div className='flex items-center gap-2'>
                                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                                                                        <polyline points="9 22 9 12 15 12 15 22"/>
                                                                    </svg>
                                                                    <span className='text-xs'><strong>Property type:</strong></span>
                                                                </div>
                                                                <span className='text-xs ml-6'>{property.property_type}</span>
                                                            </div> */}

                                                            <div className='flex flex-col gap-2 text-gray-700'>
                                                                <div className='flex items-center gap-2'>
                                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                                                    </svg>
                                                                    <span className='text-xs'><strong>Auction Open Date:</strong></span>
                                                                </div>
                                                                <span className='text-xs ml-6'>{formatDate(property.auction_date)}</span>
                                                            </div>

                                                            <div className='flex flex-col gap-2 text-gray-700'>
                                                                <div className='flex items-center gap-2'>
                                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                                                    </svg>
                                                                    <span className='text-xs'><strong>EMD Submission:</strong></span>
                                                                </div>
                                                                <span className='text-xs ml-6'>{formatEMDDate(property.emd_submission)}</span>
                                                            </div>

                                                            <div className='flex flex-col gap-2 text-gray-700'>
                                                                <div className='flex items-center gap-2'>
                                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path xmlns="http://www.w3.org/2000/svg" d="M12.9494914,6 C13.4853936,6.52514205 13.8531598,7.2212202 13.9645556,8 L17.5,8 C17.7761424,8 18,8.22385763 18,8.5 C18,8.77614237 17.7761424,9 17.5,9 L13.9645556,9 C13.7219407,10.6961471 12.263236,12 10.5,12 L7.70710678,12 L13.8535534,18.1464466 C14.0488155,18.3417088 14.0488155,18.6582912 13.8535534,18.8535534 C13.6582912,19.0488155 13.3417088,19.0488155 13.1464466,18.8535534 L6.14644661,11.8535534 C5.83146418,11.538571 6.05454757,11 6.5,11 L10.5,11 C11.709479,11 12.7183558,10.1411202 12.9499909,9 L6.5,9 C6.22385763,9 6,8.77614237 6,8.5 C6,8.22385763 6.22385763,8 6.5,8 L12.9499909,8 C12.7183558,6.85887984 11.709479,6 10.5,6 L6.5,6 C6.22385763,6 6,5.77614237 6,5.5 C6,5.22385763 6.22385763,5 6.5,5 L10.5,5 L17.5,5 C17.7761424,5 18,5.22385763 18,5.5 C18,5.77614237 17.7761424,6 17.5,6 L12.9494914,6 L12.9494914,6 Z"/>
                                                                    </svg>
                                                                    <span className='text-xs'><strong>Reserve Price:</strong></span>
                                                                </div>
                                                                <span className='text-xs ml-6'>{formatCurrency(property.reserve_price)}</span>
                                                            </div>
                                                        </div>

                                                        {/* Details Button */}
                                                        <button
                                                            onClick={() => fetchFile(property.attached_file)}
                                                            className='property-details-btn block w-full text-center text-white font-semibold py-3 transition-all duration-300 cursor-pointer'
                                                        >
                                                            DETAILS
                                                            <span className='ml-2'>›</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default AssetNewUi;